<script lang="ts" setup>
import type {PrimaryFeeling} from '@/ts/types/dto/interventions-dto';
import {computed, ref, watchEffect} from 'vue';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {FeelingOption, RatedFeelingOption} from '@/ts/types/component/feeling-selection';
import {useFeelingFuzzyMatching} from '@/ts/composables/pure/use-feeling-fuzzy-matching';
import FeelingChip from '@/vue/molecules/feeling-chip.vue';

const props = defineProps<{
    feelingOptions: PrimaryFeeling[];
}>();

const inappropriatePhraseDict: Record<string, string[]> = {
  pissed: ['angry', 'enraged', 'frustrated', 'hopeless'],
  crap: ['angry', 'enraged', 'frustrated', 'hopeless'],
  shit: ['angry', 'enraged', 'frustrated', 'hopeless'],
  sucks: ['angry', 'enraged', 'frustrated', 'hopeless'],
  damn: ['angry', 'enraged', 'frustrated', 'hopeless'],
  fucked: ['angry', 'enraged', 'frustrated', 'hopeless', 'wütend', 'frustriert'],
  beschissen: ['ärgerlich', 'wütend', 'frustriert', 'hoffnungslos'],
  scheiße: ['ärgerlich', 'wütend', 'frustriert', 'hoffnungslos'],
  kotzen: ['ärgerlich', 'wütend', 'frustriert', 'hoffnungslos'],
};

const feelingSelection = defineModel<UuidDTO[]>('feelingSelection');

const flatFeelingOptions = computed((): FeelingOption[] => {
  const seenIds = new Set<string>(); // filter duplicated IDs
  return props.feelingOptions.flatMap<FeelingOption>((primary) => {
    const secondaryFeelings = primary.secondaryFeelings.map<FeelingOption>((secondary) => ({
      id: secondary.id,
      name: secondary.name,
      related: [primary.id],
    })).filter((item: FeelingOption) => {
      // filter out, if the same name as the primary feeling
      return item.name.toLowerCase() !== primary.name.toLowerCase();
    });

    return [
      {id: primary.id, name: primary.name, related: []},
      ...secondaryFeelings,
    ];
  }).filter(item => {
    const alreadySelected = (feelingSelection.value ?? []).includes(item.id);
    const alreadyAdded = seenIds.has(item.id);
    seenIds.add(item.id);
    return !alreadyAdded && !alreadySelected;
  });
});

const query = ref<string>('');
const activeSuggestionIndex = ref<number>(-1);

// Use the shared fuzzy matching composable with feeling-specific enhancements
const filteredAndRankedOptions = useFeelingFuzzyMatching(
  flatFeelingOptions,
  query,
  inappropriatePhraseDict,
);

const activeSuggestion = computed((): RatedFeelingOption | null => {
  const index = Math.min(activeSuggestionIndex.value, filteredAndRankedOptions.value.length - 1);

  if (index < 0) {
    return null;
  }

  return filteredAndRankedOptions.value[index];
});

const isListExpanded = computed<boolean>(() => {
  return filteredAndRankedOptions.value.length > 0;
});

const clear = (): void => {
  query.value = '';
};

const onSelect = (option: RatedFeelingOption): void => {
  const uniqueIds = new Set(feelingSelection.value ?? []);
  uniqueIds.add(option.id);
  feelingSelection.value = [...uniqueIds];
  clear();
};

const onEnterInput = (): void => {
  if (activeSuggestion.value) {
    onSelect(activeSuggestion.value);
  }
};

const onBlur = (): void => {
  clear();
};

const onMoveUp = (): void => {
  if (activeSuggestionIndex.value < 0) {return;}
  const index = activeSuggestionIndex.value - 1;
  activeSuggestionIndex.value = Math.max(0, Math.min(filteredAndRankedOptions.value.length - 1, index));
};

const onMoveDown = (): void => {
  if (activeSuggestionIndex.value < 0) {return;}
  const index = activeSuggestionIndex.value + 1;
  activeSuggestionIndex.value = Math.max(0, Math.min(filteredAndRankedOptions.value.length - 1, index));
};

watchEffect(() => {
  if (filteredAndRankedOptions.value.length) {
    activeSuggestionIndex.value = 0;
  } else {
    activeSuggestionIndex.value = -1;
  }
});

const queryInputEl = ref<HTMLInputElement>();

const onMouseEnter = (): void => {
  queryInputEl.value?.focus();
};

const elUniqueId = crypto.randomUUID();
const queryInputLabelId = `feeling-selection-label-${elUniqueId}`;
const queryInputFieldId = `feeling-selection-input-${elUniqueId}`;
const suggestionListId = `feeling-selection-suggestion-list-${elUniqueId}`;

</script>

<template>
  <div class="feeling-selection" @mouseenter="onMouseEnter">
    <div class="query-input-container">
      <label :id="queryInputFieldId" class="sr-only">
        {{ $t('input_your_feelings') }}
      </label>

      <input
        :id="queryInputFieldId"
        ref="queryInputEl"
        v-model="query"
        class="query-input"
        role="combobox"
        type="text"
        aria-autocomplete="list"
        :placeholder="$t('input_your_feelings')"
        :aria-controls="suggestionListId"
        :aria-expanded="isListExpanded"
        :aria-activedescendant="activeSuggestion?.id ?? ''"
        :aria-labelledby="queryInputLabelId"
        @keydown.enter.prevent="onEnterInput"
        @keydown.up.prevent="onMoveUp"
        @keydown.down.prevent="onMoveDown"
        @blur="onBlur"
      />
    </div>


    <ul
      :id="suggestionListId"
      role="listbox"
      class="feeling-options"
      :class="{'feeling-options--expanded': isListExpanded}"
      :aria-label="$t('input_your_feelings_option_list_label')"
    >
      <li
        v-for="option in filteredAndRankedOptions"
        :id="option.id"
        :key="option.id"
        role="option"
        class="feeling-option"
        :class="{'feeling-option--focus': activeSuggestion?.id === option.id}"
        @click="onSelect(option)"
        @mousedown.prevent="() => { /* prevent blur of input field */ }"
      >
        <feeling-chip :name="option.name"/>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.feeling-selection {
  position: relative;
}

.feeling-option {
  &--focus {
    @include focus-visible-default-styling;
    border-radius: var(--button-border-radius);
  }
}

.feeling-options {
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  gap: var(--Spacing-spacing-2);
  transition:
      opacity 0.8s linear,
      max-height 0.4s ease-out;
  overflow: hidden;
  opacity: 0;
  max-height: 0;
  margin: 0;
  cursor: pointer;
  padding: var(--Spacing-spacing-3) var(--Spacing-spacing-6);

  &--expanded {
    opacity: 1;
    max-height: 100px; /* Set to a value large enough for your content */
  }
}

.query-input-container {
  padding: var(--Spacing-spacing-6);
  border-bottom: 2px solid var(--body-background-color);
}

.query-input {
  @include font-body-bold;

  // Restated after the mixin: below 1280px --Typography-Body is 14px, and iOS Safari zooms the
  // page on focusing a field under 16px without zooming back out (IPD-3064).
  font-size: max(16px, var(--Typography-Body));
  @include focus-visible;
  width: 100%;
  border-radius: var(--border-radius);
  color: var(--Neutral-UI-neutral-ui-60);
  background: transparent;
  border: none;
  padding: var(--Spacing-spacing-2);
  &::placeholder {
    color: var(--Neutral-UI-neutral-ui-60);
    opacity: 1; /* Firefox */
  }
}

</style>